import SaveIcon from "@mui/icons-material/Save";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Checkbox, FormControlLabel, Grid2, Switch, TextField, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { AGDataGrid } from "../../components/AGDataGrid";
import { AGDialog } from "../../components/AGDialog";
import { useAppSelector } from "../../redux";
import { useCreateUserRoleMutation, useEditUserRoleMutation } from "../../redux/reducers/rolesPermissions.reducer";
import { PERMISSION_TYPE_E, PermissionDetailsType } from "../../types";
import { permissionMapping } from "../../utils/constants";
import { IUserRoleRowDetails } from "./UserRoles";

interface IuserRoleDetailsDialogProps {
	open: boolean;
	userRoleDetails: IUserRoleRowDetails | null;
	allRTOAccessDisabled?: boolean;
	onClose: () => void;
}

enum SELECTED_TAB_E {
	DETAILS,
	PERMISSIONS
}

interface IPermissionRowDetails {
	id: PERMISSION_TYPE_E;
	name: string;
	view: boolean;
	edit: boolean;
	viewDisabled: boolean;
	editDisabled: boolean;
}

interface IUserRoleFormDetails {
	name: string;
	description: string;
	permissions: PermissionDetailsType;
	hasAllRTOAccess: boolean;
}

const DEFAULT_PERMISSIONS: PermissionDetailsType = {
	[PERMISSION_TYPE_E.USER_MANAGEMENT]: { view: false, edit: false },
	[PERMISSION_TYPE_E.DASHBOARD]: { view: false, edit: false },
	[PERMISSION_TYPE_E.VIOLATION_LOGS]: { view: false, edit: false },
	[PERMISSION_TYPE_E.RTO_METADATA]: { view: false, edit: false },
	[PERMISSION_TYPE_E.INTERCEPTOR_VEHICLES]: { view: false, edit: false },
	[PERMISSION_TYPE_E.USER_SESSION_MANAGEMENT]: { view: false, edit: false },
	[PERMISSION_TYPE_E.RESET_USER_ACCOUNT]: { view: false, edit: false },
	[PERMISSION_TYPE_E.USER_ACTIVITY_LOGS]: { view: false, edit: false },
	[PERMISSION_TYPE_E.SYSTEM_CONFIGURATION]: { view: false, edit: false }
};

const INVALID_VIEW_PERMISSIONS: PERMISSION_TYPE_E[] = [PERMISSION_TYPE_E.RESET_USER_ACCOUNT];
const INVALID_EDIT_PERMISSIONS: PERMISSION_TYPE_E[] = [PERMISSION_TYPE_E.USER_ACTIVITY_LOGS];

const UserRoleDetailsDialog: FC<IuserRoleDetailsDialogProps> = (props) => {
	const { open, userRoleDetails, allRTOAccessDisabled, onClose } = props;

	const authState = useAppSelector((state) => state.auth);
	const [selectedTab, setSelectedTab] = useState<SELECTED_TAB_E>(SELECTED_TAB_E.DETAILS);

	const {
		reset,
		watch,
		control,
		register,
		handleSubmit,
		formState: { errors, isDirty: inputChanged }
	} = useForm<IUserRoleFormDetails>();

	const permissions = watch("permissions");

	// APIS
	// CREATE USER ROLE
	const [
		createUserRole,
		{ isLoading: createUserRoleLoading, error: createUserRoleError, reset: resetCreateUserRoleState }
	] = useCreateUserRoleMutation();

	// EDIT USER ROLE
	const [editUserRole, { isLoading: editUserRoleLoading, error: editUserRoleError, reset: resetEditUserRoleState }] =
		useEditUserRoleMutation();

	const readOnlyInput = useMemo(
		() => !authState.user.permissions || !authState.user.permissions.user_management.edit,
		[authState.user.permissions]
	);

	const rowsData = useMemo<IPermissionRowDetails[]>(() => {
		if (!permissions) return [];

		const userPermissions = authState.user.permissions;

		return Object.entries(permissions).map(([key, details]) => ({
			id: key as PERMISSION_TYPE_E,
			name: permissionMapping[key as PERMISSION_TYPE_E],
			view: details.view,
			edit: details.edit,
			viewDisabled:
				INVALID_VIEW_PERMISSIONS.includes(key as PERMISSION_TYPE_E) ||
				!userPermissions?.[key as PERMISSION_TYPE_E]?.view,
			editDisabled:
				INVALID_EDIT_PERMISSIONS.includes(key as PERMISSION_TYPE_E) ||
				!userPermissions?.[key as PERMISSION_TYPE_E]?.edit
		}));
	}, [authState.user.permissions, permissions]);

	function handleChangeTab(tab: SELECTED_TAB_E) {
		setSelectedTab(tab);
	}

	function handleSaveButtonClick(formData: IUserRoleFormDetails) {
		const updatedPermissions = { ...formData.permissions };
		const userPermissions = authState.user.permissions;

		for (const [key, permissionDetails] of Object.entries(formData.permissions)) {
			const permissionKey = key as PERMISSION_TYPE_E;

			if (permissionDetails.view && !userPermissions?.[permissionKey].view) {
				updatedPermissions[permissionKey].view = false;
			}

			if (permissionDetails.edit && !userPermissions?.[permissionKey].edit) {
				updatedPermissions[permissionKey].edit = false;
			}
		}

		if (userRoleDetails) {
			editUserRole({
				id: userRoleDetails.id,
				data: {
					name: formData.name,
					description: formData.description,
					group_permissions: updatedPermissions,
					has_all_rto_access: formData.hasAllRTOAccess
				}
			})
				.unwrap()
				.then(() => onClose())
				.catch(() => {});
		} else {
			createUserRole({
				name: formData.name,
				description: formData.description,
				group_permissions: updatedPermissions,
				has_all_rto_access: formData.hasAllRTOAccess
			})
				.unwrap()
				.then(() => onClose())
				.catch(() => {});
		}
	}

	useEffect(() => {
		if (open) {
			reset({
				name: userRoleDetails?.name ?? "",
				description: userRoleDetails?.description ?? "",
				permissions: userRoleDetails?.permissions ?? DEFAULT_PERMISSIONS,
				hasAllRTOAccess: userRoleDetails?.has_all_rto_access ?? false
			});
		} else {
			resetCreateUserRoleState();
			resetEditUserRoleState();
			setSelectedTab(SELECTED_TAB_E.DETAILS);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, userRoleDetails]);

	const columnsData: GridColDef[] = [
		{
			field: "name",
			headerName: "Permission",
			flex: 1,
			sortable: false
		},
		{
			field: "view",
			headerName: "View",
			width: 75,
			sortable: false,
			headerAlign: "center",
			align: "center",
			renderCell: (params: GridRenderCellParams<IPermissionRowDetails, boolean>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<Controller
						control={control}
						name={`permissions.${params.row.id}.view`}
						render={({ field: { value, onChange } }) => (
							<Checkbox
								checked={value}
								disabled={params.row.viewDisabled}
								onChange={readOnlyInput ? undefined : (_event, checked) => onChange(checked)}
							/>
						)}
					/>
				</Box>
			)
		},
		{
			field: "edit",
			headerName: "Edit",
			width: 75,
			sortable: false,
			headerAlign: "center",
			align: "center",
			renderCell: (params: GridRenderCellParams<IPermissionRowDetails, boolean>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<Controller
						control={control}
						name={`permissions.${params.row.id}.edit`}
						render={({ field: { value, onChange } }) => (
							<Checkbox
								checked={value}
								disabled={params.row.editDisabled}
								onChange={readOnlyInput ? undefined : (_event, checked) => onChange(checked)}
							/>
						)}
					/>
				</Box>
			)
		}
	];

	const TabRenderer: { [key in SELECTED_TAB_E]: ReactNode } = {
		[SELECTED_TAB_E.DETAILS]: (
			<Grid2 container spacing={2} sx={{ marginTop: "1.5rem" }}>
				<Grid2 size={{ xs: 12 }}>
					<TextField
						required
						fullWidth
						size="small"
						label="Name"
						autoComplete="off"
						error={!!errors.name}
						helperText={errors.name?.message?.toString() || ""}
						slotProps={{ input: { readOnly: readOnlyInput } }}
						{...register("name", {
							required: "First name is required",
							setValueAs: (value) => (typeof value === "string" ? value.trim() : value)
						})}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						fullWidth
						multiline
						minRows={3}
						size="small"
						label="Description"
						autoComplete="off"
						error={!!errors.description}
						helperText={errors.description?.message?.toString() || ""}
						slotProps={{ input: { readOnly: readOnlyInput } }}
						{...register("description", {
							setValueAs: (value) => (typeof value === "string" ? value.trim() : value)
						})}
					/>
				</Grid2>
			</Grid2>
		),
		[SELECTED_TAB_E.PERMISSIONS]: (
			<Box sx={{ marginTop: "1rem" }}>
				<Controller
					control={control}
					name="hasAllRTOAccess"
					render={({ field: { value, onChange } }) => (
						<FormControlLabel
							control={<Switch />}
							label="All access to all RTOs"
							checked={value}
							onChange={(_, checked) => onChange(checked)}
							sx={{ marginBottom: "0.5rem" }}
							disabled={allRTOAccessDisabled}
						/>
					)}
				/>

				<AGDataGrid
					hideFooter
					hideAllSeparators
					columns={columnsData}
					rows={rowsData}
					getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 1 ? "gray-background" : "")}
				/>
			</Box>
		)
	};

	return (
		<AGDialog
			open={open}
			onClose={onClose}
			title={userRoleDetails ? "Edit User Role" : "Add New User Role"}
			color="primary"
		>
			<Box>
				<Box className="user-tabs-wrapper">
					<Box
						className={`user-tab-item ${selectedTab === SELECTED_TAB_E.DETAILS ? "selected" : ""}`}
						onClick={() => handleChangeTab(SELECTED_TAB_E.DETAILS)}
					>
						<Typography textAlign="center">User Details</Typography>
					</Box>

					<Box
						className={`user-tab-item ${selectedTab === SELECTED_TAB_E.PERMISSIONS ? "selected" : ""}`}
						onClick={() => handleChangeTab(SELECTED_TAB_E.PERMISSIONS)}
					>
						<Typography textAlign="center">Permissions</Typography>
					</Box>
				</Box>

				{TabRenderer[selectedTab]}

				{createUserRoleError ? (
					<Typography variant="body2" color="error" textAlign="center" marginTop="1rem">
						{"data" in createUserRoleError && createUserRoleError.data
							? String(createUserRoleError.data)
							: "Failed to create new user role"}
					</Typography>
				) : null}

				{editUserRoleError ? (
					<Typography variant="body2" color="error" textAlign="center" marginTop="1rem">
						{"data" in editUserRoleError && editUserRoleError.data
							? String(editUserRoleError.data)
							: "Failed to edit user role"}
					</Typography>
				) : null}

				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "1rem" }}>
					{!readOnlyInput ? (
						<Box sx={{ display: "flex", gap: "0.5rem" }}>
							<Button variant="outlined" onClick={onClose}>
								Cancel
							</Button>

							<LoadingButton
								disableElevation
								variant="contained"
								color="success"
								startIcon={<SaveIcon />}
								onClick={handleSubmit(handleSaveButtonClick)}
								loading={createUserRoleLoading || editUserRoleLoading}
								loadingPosition="start"
								disabled={!inputChanged}
							>
								Save
							</LoadingButton>
						</Box>
					) : null}
				</Box>
			</Box>
		</AGDialog>
	);
};

export default UserRoleDetailsDialog;
